import { useCallback } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Notification } from '../../../types/notifications'
import { iconForNotification } from './notificationIconMap'
import {
  DismissButton,
  ItemBody,
  ItemHeaderRow,
  ItemIconContainer,
  ItemRow,
  ItemText,
  ItemTimestamp,
  ItemTitle,
} from './styles/NotificationStyles'

interface NotificationItemProps {
  notification: Notification
  onRead: (id: string) => void
  onDismiss: (id: string) => void
  onClose: () => void
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
const WEEK_MS = 7 * DAY_MS

function formatRelative(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime()
  if (delta < MINUTE_MS) return 'just now'
  if (delta < HOUR_MS) return `${Math.floor(delta / MINUTE_MS)}m`
  if (delta < DAY_MS) return `${Math.floor(delta / HOUR_MS)}h`
  if (delta < WEEK_MS) return `${Math.floor(delta / DAY_MS)}d`
  return new Date(iso).toLocaleDateString()
}

export function NotificationItem({
  notification,
  onRead,
  onDismiss,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate()
  const Icon = iconForNotification(notification)
  const unread = notification.readAt === null
  const clickable = unread || Boolean(notification.link)

  const handleClick = useCallback(() => {
    if (unread) onRead(notification.id)
    if (notification.link) {
      void navigate(notification.link)
      onClose()
    }
  }, [unread, notification.id, notification.link, onRead, navigate, onClose])

  const handleDismiss = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onDismiss(notification.id)
    },
    [notification.id, onDismiss]
  )

  return (
    <ItemRow $unread={unread} $clickable={clickable} onClick={handleClick}>
      <ItemIconContainer aria-hidden>
        <Icon size={16} />
      </ItemIconContainer>
      <ItemBody>
        <ItemHeaderRow>
          <ItemTitle>{notification.title}</ItemTitle>
          <ItemTimestamp>{formatRelative(notification.createdAt)}</ItemTimestamp>
        </ItemHeaderRow>
        {notification.body && <ItemText>{notification.body}</ItemText>}
      </ItemBody>
      <DismissButton
        type="text"
        size="small"
        icon={<X size={14} />}
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      />
    </ItemRow>
  )
}
